<template>
    <wrapper-features-templates-one-column
    :hideWhiteBoxOnLoad="showLoader || (!tableData.length && !showLoader && !applyErrorState)"
    :showLoader="showLoader"
	:showOverlayLoader="showOverlayLoader"
    :applyErrorState="applyErrorState"
    :tokenTitleAppend="t('transfers.periodic.tokenTitleAppend')"
    :showTopRightAreaSlot="renderAccountSelector"
    @challengeSuccessHandler="successHandler"
    @challengeErrorHandler="errorHandler($event, deleteRecurring)"
    @backOrCloseClicked="emit('backOrCloseClicked')">
        <template #accounts-card-slot-area>
            <selector-accounts
            v-if="renderAccountSelector"
            onlyDropdown
            :selectedOption="selectedAccount.id"
            :options="getAccountsBalances"
            @clicked="selectedAccountId = $event"/>
        </template>
        <!-- *********************************************
        *												 *
        *                      Content                   *
        *												 *
        ********************************************** -->
        <transition-group name="common-enter-animation" mode="out-in" appear>
            <div v-if="!showLoader && !applyErrorState" class="content-wrapper prevent-auto-margins">
                <!-- Table Generic -->
                <table-generic
                :headerData="tableHeaderData"
                :tableStyles="tableStyles"
                :tableData="tableData"
                :hasMore="getRecurringTransfersHasMore"
                :isLoadingMore="tableLoadingMore"
                :loadingMoreText="t('transfers.periodic.loadingMoreText')"
                @loadMore="loadMoreHandler"
                @rowClicked="recurringTransfersClickHandler($event)"
                @buttonClicked="rowButtonClickedHandler"/>
            </div>
        </transition-group>
        <template #empty-state-slot>
            <!-- Empty State -->
            <empty-state-generic
            v-if="!tableData.length && !showLoader && !applyErrorState"
            illustration="emptyState/transfers-periodic"
            :title="t('transfers.periodic.emptyState.title')"
            :description="t('transfers.periodic.emptyState.description')"/>
        </template>
	</wrapper-features-templates-one-column>
    <!-- Recurring Transfers Detail Modal -->
    <detail-modal
    v-if="showDetailsModal"
    :selectedTransfer="selectedTransfer"
    @hideMe="showDetailsModal = false"
    @cancelRecurringTransfer="showCenteredModal = true"
    @redirectToEditFeature="redirectToEditFeature($event)"/>
    <!-- Centered Modal -->
    <modal-centered-generic
    v-if="showCenteredModal"
    negative
    rightButtonIcon="check-line"
    :title="t('transfers.periodic.cancelModal.title')"
    :text="t('transfers.periodic.cancelModal.text')"
    :leftButtonText="t('transfers.periodic.cancelModal.leftButtonText')"
    :rightButtonText="t('transfers.periodic.cancelModal.rightButtonText')"
    @hideMe="showCenteredModal = false"
    @rightButtonClicked="deleteRecurring"/>
    <!-- Feedback Toasts -->
    <toast-generic
	v-if="showFeedbackToast"
	:toastType="feedbackToastData.toastType"
	:feedbackText="feedbackToastData.text"
	:buttonsParams="feedbackToastData.buttons"
	@hideMe="showFeedbackToast = false"/>
</template>

<script setup>
import WrapperFeaturesTemplatesOneColumn from '@/components/wrappers/featuresTemplates/OneColumn'
import SelectorAccounts from '@/components/generic/selectorsWithDetails/Accounts'
import ToastGeneric from '@/components/generic/toasts/Generic'
import EmptyStateGeneric from '@/components/generic/emptyStates/Generic'
import TableGeneric from '@/components/generic/table/Generic'
import DetailModal from '@/components/features/transfers/periodic/consult/DetailModal'
import ModalCenteredGeneric from '@/components/generic/modals/centered/Generic'

/* Assets & Helpers */
import { ref, computed, onBeforeUnmount, watch } from 'vue'
import { getDate } from '@/assets/scripts/dateBuilders'
import { navigateByName } from '@/router/helpers/navigation'
import { routeList } from '@/router/helpers/routes'
import { errorObjectConstructor, emptyObjectConstructor, sucessObjectConstrutor } from '@/assets/scripts/toastsHelpers'

/* Store */
import { mapActions, mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'
import { cancelAllPendingRequests } from '@/store/helpers/accessControl'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* Emmitters */
const emit = defineEmits(['backOrCloseClicked'])

/* mapActions & mapGetters */
const { requestAccountsBalances, requestRecurringTransfers, deleteRecurringTransfer, clearFeatureStates } = mapActions(moduleMapper.TRANSFERS_PERIODIC)
const { getAccountsBalances, getRecurringTransfers, getRecurringTransfersHasMore, getLastRecurringTransfersInfo } = mapGetters(moduleMapper.TRANSFERS_PERIODIC)
const { getMobileAppState } = mapGetters()

/* Data */
const showLoader = ref(false)
const showOverlayLoader = ref(false)
const applyErrorState = ref(false)
const renderAccountSelector = ref(false)
const tableLoadingMore = ref(false)
const selectedAccountId = ref(null)
const selectedTransfer = ref(null)
const showCenteredModal = ref(false)
const showDetailsModal = ref(false)

const selectedAccount = computed(() => {
	return getAccountsBalances.value.find(account => account.id === selectedAccountId.value)
})

const redirectToEditFeature = (selectedTransfer) => {
    navigateByName(routeList.TRANSFERS_PERIODIC_EDIT_NAME, { selectedTransfer: selectedTransfer })
}

const tableHeaderData = [
    { label: t('transfers.periodic.tableHeader.description'), dataAttr: 'descriptionDebit', showInMobile: { groupWith: 'amount' }, rowCellType: 'title' },
	{ label: t('transfers.periodic.tableHeader.nextOccurrence'), dataAttr: 'nextOccurrence' },
    { label: t('transfers.periodic.tableHeader.frequencyDescription'), dataAttr: 'frequencyDescription' },
	{ label: t('transfers.periodic.tableHeader.amount'), dataAttr: 'amount', rowCellType: 'amount' },
    { dataAttr: 'button', showInMobile: true, rowCellType: 'button' },
    { dataAttr: 'tableOptions', showInMobile: true, rowCellType: 'more-options' }
]

const tableStyles = [
    getMobileAppState.value ? null : { width: '40%', 'min-width': '40%' },
    { 'text-align': 'star' },
    { 'text-align': 'star' },
    { 'text-align': 'end' },
    null,
    null
]

const tableData = computed(() => {
	return getRecurringTransfers.value.map(transfer => {
        return [
            { value: transfer.transferId, dataAttr: 'id' },
            { type: 'title', value: transfer.descriptionDebit, dataAttr: 'descriptionDebit' },
            { type: 'description', value: { content: getDate(transfer.nextOccurrence), classes: 'capitalize' }, dataAttr: 'nextOccurrence' },
            { value: transfer.frequencyDescription, dataAttr: 'frequencyDescription' },
            { type: 'amount', value: { content: transfer.amount, currency: transfer.currency, noBalanceIcon: true }, dataAttr: 'amount' },
            { type: 'button', value: 'pencil', dataAttr: 'buttonEdit' },
            { type: 'button', value: 'block', dataAttr: 'buttonCancel', hidden: false }
        ]
    })
})

const loadMoreHandler = () => {
    paginationParams.value.pageNumber++
    paginationParams.value.currentPageToken = getLastRecurringTransfersInfo.value.currentPageToken
    requestRecurring()
}

const recurringTransfersClickHandler = row => {
	selectedTransfer.value = getRecurringTransfers.value.find(recurring => recurring.transferId === row.rowId)

	showDetailsModal.value = true
}

const rowButtonClickedHandler = event => {
    if (event.dataAttr === 'buttonEdit') {
        selectedTransfer.value = getRecurringTransfers.value.find(transfer => transfer.transferId === event.rowId)
        redirectToEditFeature(selectedTransfer.value)
    } else {
        selectedTransfer.value = getRecurringTransfers.value.find(transfer => transfer.transferId === event.rowId)
        showCenteredModal.value = true
    }
}

watch(() => selectedAccountId.value, newVal => {
    if (newVal) {
        resetPaginationParams()
        requestRecurring()
    }
})

/*************************************************
*                                                *
*                 Requests Logic                 *
*                                                *
*************************************************/
const requestAccountsBalance = async () => {
    resetPaginationParams()

	showLoader.value = true
	applyErrorState.value = false
	showFeedbackToast.value = false
	cancelAllPendingRequests()

	try {
        await requestAccountsBalances()
        /* Setting Initial Selected Account */
		selectedAccountId.value = getAccountsBalances.value.find(account => { return account.default }).id
		renderAccountSelector.value = true
        requestRecurring()
	} catch (error) {
		if (error.toString() === 'Cancel') return
		applyErrorState.value = true
		errorHandler(error.response.data, requestAccountsBalance)
	}
}

const paginationParams = ref({
    pageNumber: 1,
    pageSize: 20,
    currentPageToken: ''
})

const requestRecurring = async () => {
    paginationParams.value.pageNumber === 1 ? showLoader.value = true : tableLoadingMore.value = true

    applyErrorState.value = false
	showFeedbackToast.value = false
    cancelAllPendingRequests()

    const payload = {
        ...paginationParams.value,
        accountId: selectedAccount.value.id
    }

    try {
        await requestRecurringTransfers(payload)
        showLoader.value = false
        tableLoadingMore.value = false
    } catch (error) {
        if (error.toString() === 'Cancel') return
        applyErrorState.value = true
		errorHandler(error.response.data, requestRecurring)
    }
}

const deleteRecurring = async () => {
    showOverlayLoader.value = true
    applyErrorState.value = false
    showDetailsModal.value = false

    const deleteRecurringPayload = {
        accountId: selectedAccount.value.id,
        transferId: selectedTransfer.value.transferId
    }

    try {
        await deleteRecurringTransfer(deleteRecurringPayload)
        showOverlayLoader.value = false
        showCenteredModal.value = false
    } catch (error) {
        if (error.toString() === 'Cancel') return
        applyErrorState.value = true
        errorHandler(error, deleteRecurring)
    }
}

const resetPaginationParams = () => {
    paginationParams.value = {
        pageNumber: 1,
        pageSize: 20,
        currentPageToken: ''
    }
}
/*************************************************
*                                                *
*                 Error Feedback                 *
*                                                *
*************************************************/
const showFeedbackToast = ref(false)

const feedbackToastData = ref(emptyObjectConstructor())

/*************************************************
*                                                *
*             Success/Error Handlers             *
*                                                *
*************************************************/
const successHandler = (data) => {
    requestRecurring()

    const messageToast = data && data.message ? data.message : t('transfers.periodic.cancelModal.deleteRecurringTransferSuccess')

    feedbackToastData.value = sucessObjectConstrutor(messageToast)
	showFeedbackToast.value = true
}

const errorHandler = (data, retryFn) => {
    showOverlayLoader.value = false
    showLoader.value = false

	feedbackToastData.value = errorObjectConstructor(data, retryFn)
	showFeedbackToast.value = true
}

requestAccountsBalance()

onBeforeUnmount(() => {
	clearFeatureStates()
})
</script>

<style lang="scss" scoped>
.content-wrapper {
	@include commonEnteringAnimation;
    width: 100%;
}
</style>
